import React, { useEffect, useMemo, useState } from 'react';
import { InformationCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useForm, Controller, useWatch } from 'react-hook-form';
import SearchableCategoryDropdown from './SearchableDropdown';
import CarouselList from './CarouselList';
import { useNotificationBar } from 'context/notificationContext';
import { getImageSrc } from 'services/util';
import { useShowsSearch } from 'hooks/shows-master';
import { useCollectionSearch } from 'hooks/collection';
import { useDebounce } from 'hooks/useDebounce';
import { ContentItem, Props, FormValues } from './Interface';
import IconDropdown from 'components/styledFormElements/IconDropdown';
import { X } from 'lucide-react';
import { useGetIcons } from 'hooks/useIconMaster';
import FormSelect from 'components/forms/FormSelect'
import { useGetWidgetBusinessRule } from 'hooks/business-rule';
import { useCategorySearch, useGetItemsParent } from 'hooks/category-manage';
import {
  buildWidgetLevelParentFields,
  collectManualItemParentRequests,
  collectWidgetSourceParentRequest,
  createParentCache,
  enrichWidgetItemsWithParents,
  EMPTY_PARENT_PAYLOAD,
  resolveParentsForRequests,
  shouldResolveManualItemParents,
  shouldResolveWidgetSourceParents,
} from './linkedParentResolver';

const WidgetConfig = ({ widget, onSave, items, onCancel, initialData, template_type }: Props) => {

  const MAX_ITEMS = 12;
  // const uploadMutation = useUploadFiles();
  const notification = useNotificationBar();

  /* -------- FORM -------- */
  const {
    register,
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      contentType: initialData?.data?.content_type?.toLowerCase() || 'static',
      sourceType: initialData?.data?.source_type?.toLowerCase() || 'manual',
      entityType: initialData?.data?.entity_type?.toLowerCase() || '',
      widgetIcon: initialData?.data?.widget_icon || '',
      widgetTitle: initialData?.data?.widget_title || '',
      selectedShow: null,
      selectedCollection: null,
      selectedCategory: null,
      contextualRule: initialData?.data?.content_rule_id || 0,
    },
  });

  const contentType = useWatch({ control, name: 'contentType' });
  const sourceType = useWatch({ control, name: 'sourceType' });

  const { data: iconsResponse } = useGetIcons();

  const icons =
    Array.isArray(iconsResponse?.data?.results)
      ? iconsResponse.data.results
      : [];

  const [widgetIconName, setWidgetIconName] = useState('');

  /* -------- LOCAL STATE -------- */

  const transformData = (items: any[]): ContentItem[] => {
    if (!items || !Array.isArray(items)) return [];
    return items.map((item: any) => ({
      id: item.redirection_id || item.id || 0,
      name: item.title || item.name || '',
      thumbnail: item.thumbnail || '',
      type: item.redirection_to || item.type || 'show',
      video_id: item.video_id || null,
      share_url: item?.share_url || '',
    }));
  };
  const [isFocused, setIsFocused] = useState(false);
  const [selectedItems, setSelectedItems] = useState<ContentItem[]>(() => {
    const rawItems = initialData?.data?.widget_items;
    if (rawItems?.length > 0) {
      return transformData(JSON.parse(JSON.stringify(rawItems)));
    }
    return [];
  });

  const [widgetIconPreview, setWidgetIconPreview] = useState<string | null>(
    initialData?.data?.widget_icon
      ? getImageSrc(initialData.data.widget_icon)
      : null
  );

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(
    sourceType === 'show' || sourceType === 'collection' || isFocused
      ? search
      : '',
    400
  );


  const [selectedShowObj, setSelectedShowObj] = useState<any>(null);
  const [selectedCollectionObj, setSelectedCollectionObj] = useState<any>(null);
  const [selectedCategoryObj, setSelectedCategoryObj] = useState<any>(null);
  const [commonError, setCommonError] = useState<any>(null)
  const [contentTypeArr, setContentTypeArr] = useState<any>(['static', 'contextual', 'personalized']);

  const itemsParentMutation = useGetItemsParent();

  /* -------- SEARCH -------- */

  const { data: showsResponse } =
    useShowsSearch(
      sourceType === 'show' && debouncedSearch
        ? debouncedSearch
        : ''
    );

  const { data: collectionsResponse } =
    useCollectionSearch(
      sourceType === 'collection' && debouncedSearch
        ? debouncedSearch
        : ''
    );

  const { data: categoriesResponse } =
    // useCategorySearchContent(
    useCategorySearch(
      sourceType === 'category' && debouncedSearch
        ? debouncedSearch
        : ''
    );

  const { data: businessRuleResponse, } = useGetWidgetBusinessRule({}, { type: widget?.code ? widget.code : 'video_carousel' })

  const businessWidgetRule = useMemo(() => businessRuleResponse?.data, [businessRuleResponse])
  const contextualOptions = useMemo(
    () =>
      (businessWidgetRule || []).map((r: any) => ({
        name: r.rule_name,
        value: r.slug,
        id: r.id,
      })),
    [businessWidgetRule]
  );

  const apiShows = useMemo(
    () =>
      (showsResponse?.results || []).map((s: any) => ({
        id: s.id,
        name: s.name || s.title,
      })),
    [showsResponse, isFocused]
  );

  const apiCollections = useMemo(
    () =>
      (collectionsResponse?.results || []).map((c: any) => ({
        id: c.id,
        name: c.name || c.title,
      })),
    [collectionsResponse]
  );

  const apiCategories = useMemo(
    () =>
      (categoriesResponse?.results || []).map((c: any) => ({
        id: c.id,
        name: c.name || c.title,
      })),
    [categoriesResponse]
  );

  /* -------- HELPERS -------- */

  const addItem = (item: any) => {
    setCommonError((pre) => ({ ...pre, maxLimit: '' }));
    if (selectedItems.length >= MAX_ITEMS) {
      setCommonError((pre) => ({ ...pre, maxLimit: 'You can choose only 12 items' }));
      return;
    }
    if (selectedItems.find(i => i.id === item.id && i.type === item.type)) return;

    const newItem: ContentItem = {
      id: item.id || 0,
      name: item.name || '',
      thumbnail: item.thumbnail || '',
      type: item.type || 'show',
      video_id: item.video_id || null,
      share_url: item?.share_url || '',
    };
    setSelectedItems(prev => [...prev, newItem]);
  };

  // const onIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   console.log(file);
  //   if (!file) return;

  //   if (file.size > 5 * 1024 * 1024) {
  //     setError('widgetIcon', { message: 'Max size 5MB' });
  //     return;
  //   }
  //   const allowedTypes = ['image/svg', 'image/svg+xml'];

  //   if (!allowedTypes.includes(file.type)) {
  //     setError('widgetIcon', { message: 'Invalid file type. Please upload SVG Only' });
  //     return;
  //   }
  //   setValue('widgetIcon', file, { shouldValidate: true });

  //   setWidgetIconPreview(URL.createObjectURL(file));
  // };

  const handleFocusOnShowCollection = (selction: string) => {
    setSearch('');
    setIsFocused(true)
  }

  useEffect(() => {
    if (!initialData?.data) return;

    const {
      content_type,
      source_type,
      source_id,
      source_name,
      content_rule_id
    } = initialData.data;

    // only static edit mode
    // if (content_type !== 'STATIC') return;

    // SHOW preselect
    if (source_type === 'SHOW' && source_id) {
      setValue('contentType', 'static');
      setValue('sourceType', 'show');
      setValue('selectedShow', source_id);

      setSelectedShowObj({
        id: source_id,
        name: source_name || 'Selected Show',
      });
    }

    if (content_rule_id && content_type === 'CONTEXTUAL') {
      setValue('contextualRule', content_rule_id);
    }

    // COLLECTION preselect
    if (source_type === 'COLLECTION' && source_id) {
      setValue('contentType', 'static');
      setValue('sourceType', 'collection');
      setValue('selectedCollection', source_id);
      setSelectedCollectionObj({
        id: source_id,
        name: source_name || 'Selected Collection',
      });
    }

    // CATEGORY preselect
    if (source_type === 'CATEGORY' && source_id) {
      setValue('contentType', 'static');
      setValue('sourceType', 'category');
      setValue('selectedCategory', source_id);
      setSelectedCategoryObj({
        id: source_id,
        name: source_name || 'Selected Category',
      });
    }

  }, [initialData, setValue]);



  const widgetIconValue = useWatch({ control, name: 'widgetIcon' });

  useEffect(() => {
    if (!widgetIconValue || !icons.length) {
      setWidgetIconName('');
      return;
    }

    const cleanFileName = widgetIconValue.split('/').pop() || '';

    const matchedIcon = icons.find((icon: any) => {
      const iconValue =
        icon.path || icon.image || icon.icon || '';

      return iconValue.includes(cleanFileName);
    });

    if (matchedIcon) {
      setWidgetIconName(matchedIcon.name || '');
    } else {
      setWidgetIconName('');
    }
  }, [widgetIconValue, icons]);


  useEffect(() => {
    if (!widgetIconValue) {
      setWidgetIconPreview(null);
      return;
    }

    // If uploaded file
    if (typeof widgetIconValue === 'object') {
      setWidgetIconPreview(URL.createObjectURL(widgetIconValue));
      return;
    }

    // If string path
    const previewURL = widgetIconValue.startsWith('http')
      ? widgetIconValue
      : getImageSrc(widgetIconValue);

    setWidgetIconPreview(previewURL);
  }, [widgetIconValue]);
  // useEffect(() => {
  //   if (contentType === 'contextual' && contextualOptions.length > 0 && !initialData?.data?.content_rule_id) {
  //     setValue('contextualRule', contextualOptions[0].id);
  //   }
  // }, [contentType, contextualOptions, setValue]);

  useEffect(() => {
    if (template_type === 'HOME' || template_type === 'EXPLORE') {
      setContentTypeArr(['static', 'personalized']);
    }
  }, [template_type])

  /* -------- SUBMIT -------- */
  const onSubmit = async (form: FormValues) => {
    let icon = initialData?.data?.widget_icon || '';
    let source_name = initialData?.data?.source_name || '';
    let widgetItems: any[] = [];
    let content_rule: string = '';
    let content_rule_id: any = '';
    let content_type: any = '';
    let source_type: any = '';
    let source_id: any = '';
    let widgetLevelParents = { ...EMPTY_PARENT_PAYLOAD };

    const getRelativeIconPath = (iconPath: string) => {
      if (!iconPath) return '';
      try {
        if (iconPath.startsWith('http')) {
          return new URL(iconPath).pathname.replace(/^\/+/, '');
        }
        return iconPath;
      } catch {
        return iconPath;
      }
    };

    if (contentType === 'static' && sourceType === 'manual') {
      if (selectedItems?.length === 0) {
        setCommonError((pre) => ({ ...pre, itemError: 'Please select at least one item' }));
        return;
      }

      widgetItems = selectedItems.map((i) => ({
        type: i.type,
        thumbnail: i.thumbnail,
        title: i.name,
        video_id: i?.video_id || '',
        redirection_to: i.type ? i.type.toUpperCase() : '',
        redirection_id: i.id,
        share_url: i?.share_url || '',
      }));

      if (typeof form.widgetIcon === 'string') {
        icon = getRelativeIconPath(form.widgetIcon);
      } else {
        icon = '';
      }
    }

    if (contentType === 'static') {
      if (sourceType === 'show') {
        source_name = selectedShowObj?.name || '';
        source_id = form.selectedShow;
        source_type = form.sourceType;
      } else if (sourceType === 'collection') {
        source_name = selectedCollectionObj?.name || '';
        source_id = form.selectedCollection;
        source_type = form.sourceType;
      } else if (sourceType === 'category') {
        source_name = selectedCategoryObj?.name || '';
        source_id = form.selectedCategory;
        source_type = form.sourceType;
      } else if (sourceType === 'manual') {
        source_type = form.sourceType;
        source_name = '';
        source_id = '';
      }
    }

    if (contentType === 'contextual') {
      content_rule_id = form.contextualRule;
      source_type = form.sourceType;
      const selectedRule = contextualOptions.find(
        (rule) => rule.id === form.contextualRule
      );
      content_rule = selectedRule?.value || '';
    }

    if (contentType === 'personalized') {
      source_type = '';
      if (typeof form.widgetIcon === 'string') {
        icon = getRelativeIconPath(form.widgetIcon);
      } else {
        icon = '';
      }
    }

    const needsManualParents = shouldResolveManualItemParents(contentType, sourceType);
    const needsSourceParents = shouldResolveWidgetSourceParents(contentType, sourceType);

    if (needsManualParents || needsSourceParents) {
      try {
        const cache = createParentCache();
        const requests = [
          ...collectWidgetSourceParentRequest(sourceType, source_id),
          ...(needsManualParents ? collectManualItemParentRequests(selectedItems) : []),
        ];

        await resolveParentsForRequests(
          itemsParentMutation.mutateAsync,
          cache,
          requests
        );

        if (needsManualParents) {
          widgetItems = enrichWidgetItemsWithParents(widgetItems, cache);
        }

        if (needsSourceParents) {
          widgetLevelParents = buildWidgetLevelParentFields(sourceType, cache, {
            sourceId: source_id,
            sourceName: source_name,
          });
        }
      } catch {
        notification.push({
          title: 'Error',
          message: 'Failed to resolve parent data. Please try again.',
          type: 'error',
        });
        return;
      }
    } else if (contentType === 'static' && sourceType === 'category') {
      widgetLevelParents = buildWidgetLevelParentFields('category', createParentCache(), {
        sourceId: source_id,
        sourceName: source_name,
      });
    }

    const finalData = {
      name: widget.label,
      widget_code: widget?.code,
      widget_type: Number(widget.value),
      data: {
        widget_title: form.widgetTitle,
        widget_code: widget?.code,
        widget_icon: icon,
        view_all: false,
        content_rule: content_rule ? content_rule : '',
        content_rule_id: content_rule_id ? content_rule_id : '',
        content_type: form.contentType.toUpperCase(),
        entity_type: form.entityType ? form.entityType.toUpperCase() : '',
        source_type: source_type ? source_type.toUpperCase() : '',
        source_name: source_name ? source_name : '',
        source_id: source_id ? source_id : '',
        ...widgetLevelParents,
        widget_items: widgetItems,
      },
    };
    onSave(JSON.parse(JSON.stringify(finalData)));

    notification.push({
      title: 'Success',
      message: 'Video Carousel widget saved successfully',
      type: 'success',
    });
  };

  /* ---------------- JSX ---------------- */

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">

          {/* LEFT PANEL */}
          <div className="lg:col-span-3 space-y-3">

            <div className="rounded-lg border bg-white p-4 space-y-6 dark:bg-slate-900 dark:border-slate-700">

              {/* CONTENT TYPE */}
              <div>
                <label className="justify-center  block text-sm font-medium mb-3 dark:text-white">
                  Content Type
                  <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                      Static (fixed admin content), Contextual (page-based), Personalized (user recommendations). Choose based on content strategy.
                    </div>
                  </span>
                </label>

                <div className="flex gap-6">
                  {contentTypeArr.map(type => (
                    <label key={type} className="flex items-center gap-2">
                      <input
                        type="radio"
                        value={type}
                        {...register('contentType')}
                        className="w-4 h-4"
                      />
                      <span className="capitalize text-sm dark:text-white">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* SOURCE TYPE */}
              {contentType === 'static' && (
                <div>
                  <label className="block text-sm font-medium mb-3 dark:text-white">Source Type
                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                      <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                        Show (auto-pulls episodes), Collection (auto-pulls shows), Manual (custom ordered list). Single entity auto-sources title/icon.
                      </div>
                    </span>
                  </label>
                  <div className="flex gap-4">
                    {['show', 'collection', 'category', 'manual'].map(type => (
                      <label key={type} className="flex items-center gap-2">
                        <input
                          type="radio"
                          value={type}
                          {...register('sourceType')}
                          className="w-4 h-4"
                        />
                        <span className="capitalize text-sm dark:text-white">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* SHOW */}
              {contentType === 'static' && sourceType === 'show' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium">
                    Select Show
                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                      <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                        Select a single show. Carousel auto-populates with all episodes from that show, ordered by episode priority.
                      </div>
                    </span>
                  </label>

                  {!selectedShowObj && (
                    <input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      onFocus={() => handleFocusOnShowCollection('show')}
                      placeholder="Search shows..."
                      className="w-full px-4 py-2 border rounded-lg text-sm"
                    />
                  )}

                  <Controller
                    name="selectedShow"
                    control={control}
                    rules={{ required: 'Show required' }}
                    render={({ field }) => (
                      <>
                        {!selectedShowObj && search && (
                          <div className="border rounded-lg max-h-48 overflow-y-auto">
                            {apiShows.map(show => (
                              <button
                                key={show.id}
                                type="button"
                                onClick={() => {
                                  field.onChange(show.id);
                                  setSelectedShowObj(show);
                                  setSearch('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                              >
                                {show.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  />

                  {errors?.selectedShow && (
                    <p className="text-sm text-red-500">
                      {errors.selectedShow.message}
                    </p>
                  )}

                  {selectedShowObj && (
                    <div className="flex justify-between bg-blue-50 border rounded-lg px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          {selectedShowObj?.name}
                        </p>
                        <p className="text-xs text-blue-600">
                          ID: {selectedShowObj?.id}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedShowObj(null);
                          setValue('selectedShow', null);
                        }}
                      >
                        <TrashIcon className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  )}

                </div>
              )}
              {/* COLLECTION */}
              {contentType === 'static' && sourceType === 'collection' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium">
                    Select Collection
                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                      <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                      <div
                        className="
            absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64
            rounded-md bg-white border border-slate-200
            p-3 text-xs text-slate-700 shadow-lg
            opacity-0 invisible group-hover:opacity-100 group-hover:visible
          "
                      >
                        Select a single collection. Carousel auto-populates with all shows in this collection.
                      </div>
                    </span>
                  </label>

                  {/* Search Input */}
                  {!selectedCollectionObj && (
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onFocus={() => handleFocusOnShowCollection('collection')}
                      placeholder="Search collections..."
                      className="w-full px-4 py-2 border rounded-lg text-sm"
                    />
                  )}

                  <Controller
                    name="selectedCollection"
                    control={control}
                    rules={{ required: 'Collection is required' }}
                    render={({ field }) => (
                      <>
                        {!selectedCollectionObj && search && (
                          <div className="bg-white border rounded-lg max-h-48 overflow-y-auto">
                            {apiCollections.map((col) => (
                              <button
                                key={col.id}
                                type="button"
                                onClick={() => {
                                  field.onChange(col.id);
                                  setSelectedCollectionObj(col);
                                  setSearch('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                              >
                                {col.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  />

                  {errors?.selectedCollection && (
                    <p className="text-sm text-red-500">
                      {errors.selectedCollection.message}
                    </p>
                  )}

                  {/* Selected Collection Card */}
                  {selectedCollectionObj && (
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          {selectedCollectionObj.name}
                        </p>
                        <p className="text-xs text-blue-600">
                          ID: {selectedCollectionObj.id}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCollectionObj(null);
                          setValue('selectedCollection', null);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* CATEGORY */}
              {contentType === 'static' && sourceType === 'category' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium">
                    Select Category
                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                      <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                      <div
                        className="
            absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64
            rounded-md bg-white border border-slate-200
            p-3 text-xs text-slate-700 shadow-lg
            opacity-0 invisible group-hover:opacity-100 group-hover:visible
          "
                      >
                        Select a single category. Carousel auto-populates with all shows in this category.
                      </div>
                    </span>
                  </label>

                  {/* Search Input */}
                  {!selectedCategoryObj && (
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onFocus={() => handleFocusOnShowCollection('collection')}
                      placeholder="Search category..."
                      className="w-full px-4 py-2 border rounded-lg text-sm"
                    />
                  )}

                  <Controller
                    name="selectedCategory"
                    control={control}
                    rules={{ required: 'Category is required' }}
                    render={({ field }) => (
                      <>
                        {!selectedCategoryObj && search && (
                          <div className="bg-white border rounded-lg max-h-48 overflow-y-auto">
                            {apiCategories.map((col) => (
                              <button
                                key={col.id}
                                type="button"
                                onClick={() => {
                                  field.onChange(col.id);          // RHF value
                                  setSelectedCategoryObj(col);  // UI object
                                  setSearch('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                              >
                                {col.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  />

                  {errors?.selectedCategory && (
                    <p className="text-sm text-red-500">
                      {errors.selectedCategory.message}
                    </p>
                  )}

                  {/* Selected Category Card */}
                  {selectedCategoryObj && (
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          {selectedCategoryObj.name}
                        </p>
                        <p className="text-xs text-blue-600">
                          ID: {selectedCategoryObj.id}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCategoryObj(null);
                          setValue('selectedCategory', null);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* MANUAL */}
              {contentType === 'static' && sourceType === 'manual' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-white">Widget Title  <span className="text-red-700">*</span>
                      <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                          Static free text or auto-source from selected show/collection name.
                        </div>
                      </span>
                    </label>
                    <input
                      {...register('widgetTitle', { required: 'Widget Title required' })}
                      className="w-full px-4 py-2 border rounded-lg text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    {errors?.widgetTitle && (
                      <p className="text-sm text-red-500">
                        {errors.widgetTitle.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block dark:text-white">
                      Widget Icon <span className="text-red-700">*</span>
                    </label>

                    <Controller
                      name="widgetIcon"
                      control={control}
                      rules={{ required: 'Widget icon is required' }}
                      render={({ field }) => {
                        const iconValue = field.value;

                        return (
                          <div className="relative">
                            {iconValue ? (
                              <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-white dark:bg-slate-700 dark:border-slate-600">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={
                                      typeof iconValue === 'string' && iconValue.startsWith('http')
                                        ? iconValue
                                        : typeof iconValue === 'string'
                                          ? getImageSrc(iconValue)
                                          : ''
                                    }
                                    alt="Selected icon"
                                    className="w-6 h-6 object-contain dark:bg-white"
                                  />
                                  <span className="text-sm dark:text-white text-gray-700">
                                    {widgetIconName || 'Icon selected'}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => {
                                    field.onChange('');
                                    setWidgetIconPreview('');
                                    clearErrors('widgetIcon');
                                  }}
                                  className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <IconDropdown

                                onChange={(selectedIcon: any) => {
                                  const iconKey =
                                    selectedIcon?.path ||
                                    selectedIcon?.image ||
                                    selectedIcon?.icon ||
                                    '';

                                  field.onChange(iconKey);

                                  if (iconKey) {
                                    const preview = iconKey.startsWith('http')
                                      ? iconKey
                                      : getImageSrc(iconKey);

                                    setWidgetIconPreview(preview);
                                    clearErrors('widgetIcon');
                                  } else {
                                    setWidgetIconPreview('');
                                  }
                                }}
                              />
                            )}
                          </div>
                        );
                      }}
                    />

                    {errors?.widgetIcon && (
                      <p className="text-sm text-red-500">
                        {errors.widgetIcon.message}
                      </p>
                    )}
                  </div>
                </>
              )}

              {contentType === 'contextual' && (
                <>
                  <div className="space-y-2">
                    <FormSelect
                      label="Select Widget Rule"
                      name="contextualRule"
                      options={contextualOptions}
                      register={register('contextualRule', {
                        required: 'Contextual rule is required',
                        valueAsNumber: true,
                        validate: (value: any) =>
                          (value !== '' && value !== 0) || 'Please select a widget rule'
                      })}
                      defaultOptionLabel="Select Option"
                      errors={errors as any}
                      helpText='Contextual mode allows a widget to automatically adapt to the entity context of the page where it is placed and surface discovery-oriented content within that scope.'
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-3">Source Type
                      <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                          show or episodes for current collection page or category page.
                        </div>
                      </span>
                    </label>
                    <div className="flex gap-4">
                      {['show', 'episode'].map(type => (
                        <label key={type} className="flex items-center gap-2">
                          <input
                            type="radio"
                            value={type}
                            // {...register('sourceType')}
                            {...register('sourceType', {
                              required: 'Please select source type'
                            })}
                            className="w-4 h-4"
                          />
                          <span className="capitalize text-sm">{type}</span>
                        </label>
                      ))}
                    </div>
                    {/* Error Message */}
                    {errors.sourceType && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.sourceType.message}
                      </p>
                    )}
                  </div>
                </>
              )}

              {contentType === 'personalized' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-white">Widget Title  <span className="text-red-700">*</span>
                      <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                          Title displayed for the personalized carousel.
                        </div>
                      </span>
                    </label>
                    <input
                      {...register('widgetTitle', { required: 'Widget Title required' })}
                      className="w-full px-4 py-2 border rounded-lg text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    {errors?.widgetTitle && (
                      <p className="text-sm text-red-500">
                        {errors.widgetTitle.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-3 dark:text-white">Entity Type <span className="text-red-700">*</span>
                      <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                          Choose which entity type should drive personalized recommendations.
                        </div>
                      </span>
                    </label>
                    <div className="flex gap-4">
                      {['show', 'collection'].map(type => (
                        <label key={type} className="flex items-center gap-2">
                          <input
                            type="radio"
                            value={type}
                            {...register('entityType', {
                              required: 'Entity Type is required',
                            })}
                            className="w-4 h-4"
                          />
                          <span className="capitalize text-sm dark:text-white">{type}</span>
                        </label>
                      ))}
                    </div>
                    {errors?.entityType && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.entityType.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block dark:text-white">
                      Widget Icon <span className="text-red-700">*</span>
                    </label>

                    <Controller
                      name="widgetIcon"
                      control={control}
                      rules={{ required: 'Widget icon is required' }}
                      render={({ field }) => {
                        const iconValue = field.value;

                        return (
                          <div className="relative">
                            {iconValue ? (
                              <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-white dark:bg-slate-700 dark:border-slate-600">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={
                                      typeof iconValue === 'string' && iconValue.startsWith('http')
                                        ? iconValue
                                        : typeof iconValue === 'string'
                                          ? getImageSrc(iconValue)
                                          : ''
                                    }
                                    alt="Selected icon"
                                    className="w-6 h-6 object-contain dark:bg-white"
                                  />
                                  <span className="text-sm dark:text-white text-gray-700">
                                    {widgetIconName || 'Icon selected'}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => {
                                    field.onChange('');
                                    setWidgetIconPreview('');
                                    clearErrors('widgetIcon');
                                  }}
                                  className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <IconDropdown
                                onChange={(selectedIcon: any) => {
                                  const iconKey =
                                    selectedIcon?.path ||
                                    selectedIcon?.image ||
                                    selectedIcon?.icon ||
                                    '';

                                  field.onChange(iconKey);

                                  if (iconKey) {
                                    const preview = iconKey.startsWith('http')
                                      ? iconKey
                                      : getImageSrc(iconKey);

                                    setWidgetIconPreview(preview);
                                    clearErrors('widgetIcon');
                                  } else {
                                    setWidgetIconPreview('');
                                  }
                                }}
                              />
                            )}
                          </div>
                        );
                      }}
                    />

                    {errors?.widgetIcon && (
                      <p className="text-sm text-red-500">
                        {errors.widgetIcon.message}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>


          {/* RIGHT PANEL */}
          <div className="lg:col-span-3 space-y-3">
            {contentType === 'static' && sourceType === 'manual' && (
              <>
                {/* ADD SHOWS & EPISODES */}
                <div>
                  <label className="block text-sm font-medium mb-2 dark:text-white">
                    Shows & Episodes
                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                      <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                      <div
                        className="
                    absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64
                    rounded-md bg-white border p-3 text-xs shadow-lg
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 text-slate-700
                    "
                      >
                        Search and add shows or episodes. Drag to reorder after adding.
                      </div>
                    </span>
                  </label>
                  <SearchableCategoryDropdown
                    value={{ name: '', id: '' }}
                    onSelect={addItem}
                    selectedItems={selectedItems}
                  />
                  {commonError?.maxLimit && (
                    <p className="text-sm text-red-500 mt-1">
                      {commonError?.maxLimit}
                    </p>)}
                </div>
                <div>
                  <div className='flex items-center justify-between'>
                    <label className="block text-sm font-medium mb-2 dark:text-white">
                      Selected Items
                    </label>


                    {/* Tooltip */}
                    <div className="relative flex-item group mb-2 ml-2 mt-0">
                      <span
                        className={`
                rounded-full px-2 py-0.5 text-xs font-medium
                ${selectedItems.length >= MAX_ITEMS
                            ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700'}
            `}
                      >
                        {selectedItems.length}/{MAX_ITEMS}
                      </span>

                      <div className="absolute bottom-full right-0 -translate-y-0 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50
                                                                    ">
                        You can select up to {MAX_ITEMS} items <span className='font-medium'>({selectedItems.length} selected)</span>.
                      </div>
                    </div>
                  </div>



                  <div className="bg-white border border-gray-300 rounded-lg p-2 min-h-32 dark:bg-slate-800 dark:border-slate-600">
                    {selectedItems.length === 0 ? (
                      <p className="text-sm text-red-500">
                        Search and add shows or episodes to populate the carousel {commonError?.itemError ? commonError.itemError : ''}
                      </p>
                    ) : (
                      <div
                        className="
              space-y-4
              max-h-[400px]
              overflow-y-auto
              pr-2
            "
                      >
                        <CarouselList
                          items={selectedItems}
                          getImageSrc={getImageSrc}
                          setItems={setSelectedItems}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="sticky bottom-0 bg-white border-t p-4 mt-6 flex justify-end gap-3 dark:bg-slate-800 dark:border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md bg-slate-200 px-5 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-md bg-blue-600 px-5 py-2 text-sm text-white"
          >
            Save
          </button>
        </div>
      </div>
    </form>
  );
};

export default WidgetConfig;
